import { useState, useEffect } from 'react';
import { ChevronDown, Shield } from 'lucide-react';
import { CATEGORIES, getProductsByCategory } from '../data/products';
import { fetchProducts } from '../services/api';
import ProductCard from '../components/ProductCard';

const SORT_OPTIONS = [
  { val: 'featured', label: 'Featured' },
  { val: 'price-asc', label: 'Price: Low to High' },
  { val: 'price-desc', label: 'Price: High to Low' },
  { val: 'rating', label: 'Highest Rated' },
];

export default function CategoryPage({ categorySlug }) {
  const [sort, setSort] = useState('featured');
  const [apiProducts, setApiProducts] = useState(null);
  const cat = CATEGORIES.find(c => c.slug === categorySlug);

  const catMap = { 'suppressors': 'suppressors', 'sound-mitigation-equipment': 'sme', 'clamp-ons': 'clamp-ons', 'muzzle-devices': 'muzzle-devices' };
  const catId = catMap[categorySlug] || categorySlug;

  useEffect(() => {
    fetchProducts(catId)
      .then(prods => { if (prods?.length) setApiProducts(prods); })
      .catch(() => {});
  }, [catId]);

  let products = apiProducts || getProductsByCategory(categorySlug);

  if (sort === 'price-asc') products = [...products].sort((a, b) => a.price - b.price);
  else if (sort === 'price-desc') products = [...products].sort((a, b) => b.price - a.price);
  else if (sort === 'rating') products = [...products].sort((a, b) => b.rating - a.rating);

  if (!cat) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] pt-20 flex items-center justify-center">
        <div className="text-center">
          <p className="font-bebas text-3xl text-zinc-600 tracking-wider">CATEGORY NOT FOUND</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] pt-16 lg:pt-20">
      {/* Category Hero */}
      <div
        data-testid="category-hero"
        className="relative overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(135deg, rgba(0,0,0,0.92) 0%, rgba(0,0,0,0.6) 60%, rgba(0,0,0,0.85) 100%), url("${cat.image}")`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="max-w-2xl">
            <div className="flex items-center gap-3 mb-4">
              {cat.nfaRequired && (
                <span className="flex items-center gap-1 font-inter text-xs font-semibold text-orange-400 bg-orange-500/20 border border-orange-500/30 px-3 py-1">
                  <Shield size={11} /> NFA Item
                </span>
              )}
              {!cat.nfaRequired && (
                <span className="font-inter text-xs font-semibold text-green-400 bg-green-500/20 border border-green-500/30 px-3 py-1">
                  No NFA Required
                </span>
              )}
            </div>
            <h1 data-testid="category-heading" className="font-bebas text-5xl lg:text-6xl text-white tracking-wider mb-3">
              {cat.name}
            </h1>
            <p className="font-inter text-base text-zinc-300 leading-relaxed max-w-xl mb-4">
              {cat.description}
            </p>
            <p className="font-inter text-xs text-zinc-500 uppercase tracking-widest">
              {products.length} product{products.length !== 1 ? 's' : ''} available
            </p>
          </div>
        </div>
      </div>

      {/* NFA Notice for suppressors */}
      {cat.nfaRequired && (
        <div className="bg-orange-500/10 border-b border-orange-500/20">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-start gap-3">
            <Shield size={15} className="text-orange-500 flex-shrink-0 mt-0.5" />
            <p className="font-inter text-xs text-zinc-400">
              <strong className="text-orange-400">NFA Notice:</strong> All suppressors/silencers are NFA-regulated items requiring ATF Form 4 transfer through a licensed FFL/SOT dealer. An ATF approval period applies. All NFA rules apply. You must select UPS shipping at checkout.
            </p>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-6 py-10">
        {/* Toolbar */}
        <div className="flex items-center justify-between mb-8">
          <span className="font-inter text-xs text-zinc-600 uppercase tracking-widest">
            Showing {products.length} product{products.length !== 1 ? 's' : ''}
          </span>
          <div className="relative">
            <select
              data-testid="category-sort"
              value={sort}
              onChange={e => setSort(e.target.value)}
              className="font-inter text-sm bg-[#1e1e1e] border border-white/10 text-zinc-300 pl-4 pr-8 py-2 appearance-none cursor-pointer"
            >
              {SORT_OPTIONS.map(o => <option key={o.val} value={o.val}>{o.label}</option>)}
            </select>
            <ChevronDown size={14} className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
          </div>
        </div>

        {/* Grid */}
        <div data-testid="category-product-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {products.map(product => (
            <ProductCard key={product.id} product={product} />
          ))}
        </div>

        {products.length === 0 && (
          <div className="text-center py-20">
            <p className="font-bebas text-2xl text-zinc-600 tracking-wider">NO PRODUCTS IN THIS CATEGORY YET</p>
          </div>
        )}

        {/* Category education block */}
        <div className="mt-16 bg-[#111111] border border-white/10 p-8">
          <h2 className="font-bebas text-3xl text-white tracking-wider mb-4">
            ABOUT {cat.name.toUpperCase()}
          </h2>
          <p className="font-inter text-sm text-zinc-400 leading-relaxed">
            {cat.description}
          </p>
        </div>
      </div>
    </div>
  );
}
